import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Check, Trash2 } from 'lucide-react';
import '../home/continue-menu.css';

export interface YomiriActionMenuOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  color?: string;
}

export interface YomiriActionMenuProps {
  open: boolean;
  anchorEl?: HTMLElement | null;
  anchorPosition?: { left: number; top: number } | null;
  onClose: () => void;
  options: YomiriActionMenuOption[];
  selectedValue?: string | null;
  onSelect: (value: string) => void;
  title?: string;
  allowClear?: boolean;
  clearLabel?: string;
  onClear?: () => void;
}

export function YomiriActionMenu({
  open,
  anchorEl,
  anchorPosition,
  onClose,
  options,
  selectedValue,
  onSelect,
  title,
  allowClear,
  clearLabel = 'Quitar de la lista',
  onClear,
}: YomiriActionMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open) return;

    let targetLeft = 0;
    let targetTop = 0;

    if (anchorPosition) {
      targetLeft = anchorPosition.left;
      targetTop = anchorPosition.top;
    } else if (anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      targetLeft = rect.left;
      targetTop = rect.bottom + 6;
    }

    const menuEl = menuRef.current;
    if (menuEl) {
      const menuWidth = menuEl.offsetWidth || 230;
      const menuHeight = menuEl.offsetHeight || 200;
      const padding = 12;

      if (targetLeft + menuWidth > window.innerWidth - padding) {
        targetLeft = Math.max(padding, window.innerWidth - menuWidth - padding);
      }
      if (targetTop + menuHeight > window.innerHeight - padding) {
        if (anchorEl) {
          const rect = anchorEl.getBoundingClientRect();
          targetTop = Math.max(padding, rect.top - menuHeight - 6);
        } else {
          targetTop = Math.max(padding, window.innerHeight - menuHeight - padding);
        }
      }
    }

    setCoords({
      left: Math.max(8, targetLeft),
      top: Math.max(8, targetTop),
    });
  }, [open, anchorPosition, anchorEl]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Back' || e.keyCode === 10009 || e.keyCode === 461) {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const items = menuRef.current?.querySelectorAll<HTMLButtonElement>('.yomiri-context-menu-item');
        if (!items || items.length === 0) return;

        const currentActive = document.activeElement;
        const currentIndex = Array.from(items).indexOf(currentActive as HTMLButtonElement);

        let nextIndex = 0;
        if (e.key === 'ArrowDown') {
          nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        } else {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        }

        items[nextIndex]?.focus();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node) && !anchorEl?.contains(e.target as Node)) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    const timer = setTimeout(() => {
      window.addEventListener('pointerdown', handleClickOutside, true);
    }, 10);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      clearTimeout(timer);
      window.removeEventListener('pointerdown', handleClickOutside, true);
    };
  }, [open, onClose, anchorEl]);

  useEffect(() => {
    if (open) {
      const activeItem = menuRef.current?.querySelector<HTMLButtonElement>('.yomiri-context-menu-item.is-selected')
        || menuRef.current?.querySelector<HTMLButtonElement>('.yomiri-context-menu-item');
      activeItem?.focus();
    }
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      ref={menuRef}
      className="yomiri-context-menu"
      style={{
        top: `${coords.top}px`,
        left: `${coords.left}px`,
      }}
      role="menu"
      aria-label={title || 'Menú de opciones'}
    >
      {title && (
        <div className="yomiri-context-menu-header" title={title}>
          {title}
        </div>
      )}

      {options.map((option) => {
        const isSelected = selectedValue === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="menuitem"
            className={`yomiri-context-menu-item ${isSelected ? 'is-selected' : ''}`}
            onClick={() => {
              onSelect(option.value);
              onClose();
            }}
          >
            {option.icon && <span className="yomiri-menu-icon" style={{ color: option.color }}>{option.icon}</span>}
            <span style={{ flex: 1, textAlign: 'left', fontWeight: isSelected ? 600 : 500 }}>
              {option.label}
            </span>
            {isSelected && <Check size={15} style={{ color: '#ff2e63', flexShrink: 0 }} />}
          </button>
        );
      })}

      {allowClear && selectedValue && (
        <>
          <div className="yomiri-context-menu-divider" />
          <button
            type="button"
            role="menuitem"
            className="yomiri-context-menu-item yomiri-context-menu-item-danger"
            onClick={() => {
              onClear?.();
              onClose();
            }}
          >
            <Trash2 size={15} />
            <span>{clearLabel}</span>
          </button>
        </>
      )}
    </div>,
    document.body
  );
}
